import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useCompanies } from './hooks';
import { useCurrentDomain } from "./context/DomainContext";
import { getReferralTemplatesByCompany, assignReferralTemplates, deleteReferralTemplateMapping } from './services/masterDataService';
import { selectStyles as sharedSelectStyles } from './helpers';
import { THEME } from './theme/themeColors';

const selectStyles = {
    ...sharedSelectStyles,
    clearIndicator: (base) => ({ ...base, padding: '0 4px' }),
};

const cardHeaderStyle = {
    color: THEME.onPrimary, fontWeight: 600, fontSize: 14, padding: '8px', backgroundColor: THEME.primary,
};

const Field = ({ label, required, htmlFor }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <label htmlFor={htmlFor} style={{ fontSize: '10px', fontWeight: 600, color: 'black', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 0 }}>
            {label}
            {required && <span style={{ color: '#dc3545', marginLeft: '3px', fontSize: '14px', lineHeight: '1' }}>*</span>}
        </label>
    </div>
);

const Spinner = ({ size = 'sm', color = THEME.primary }) => (
    <div className="text-center py-4">
        <div className={`spinner-border spinner-border-${size}`} style={{ color }} role="status">
            <span className="visually-hidden">Loading...</span>
        </div>
    </div>
);

const ConfigureReferralTemplate = () => {
    const { domain } = useCurrentDomain();
    const [referralTemplates, setReferralTemplates] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedTemplateIds, setSelectedTemplateIds] = useState([]);
    const [mappedTemplates, setMappedTemplates] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [templateSearch, setTemplateSearch] = useState('');
    const [addLoading, setAddLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [templatesLoading, setTemplatesLoading] = useState(false);

    const fetchSeqRef = useRef(0);

    const { companies, loading: companiesLoading } = useCompanies(domain);

    useEffect(() => {
        setSelectedCompany(null);
        setTemplateSearch('');
    }, [domain]);

    useEffect(() => {
        if (!selectedCompany) {
            fetchSeqRef.current++;
            setReferralTemplates([]);
            setMappedTemplates([]);
            setSelectedTemplateIds([]);
            setSelectedRows([]);
            setTemplatesLoading(false);
            return;
        }
        fetchTemplates();
    }, [selectedCompany]);

    const fetchTemplates = async () => {
        const seq = ++fetchSeqRef.current;
        const companyId = selectedCompany.value;
        setTemplatesLoading(true);
        try {
            const data = await getReferralTemplatesByCompany(companyId, domain);
            if (seq !== fetchSeqRef.current) return;
            setReferralTemplates(data.filter(t => !t.isAssigned));
            setMappedTemplates(data.filter(t => t.isAssigned));
            setSelectedTemplateIds([]);
            setSelectedRows([]);
        } catch (err) {
            if (seq !== fetchSeqRef.current) return;
            toast.error('Failed to load referral templates.');
        } finally {
            if (seq === fetchSeqRef.current) setTemplatesLoading(false);
        }
    };

    const handleAddMapping = async () => {
        if (!selectedCompany || selectedTemplateIds.length === 0) return;
        setAddLoading(true);
        const loadingToast = toast.loading('Assigning referral templates...');
        try {
            await assignReferralTemplates(selectedCompany.value, selectedTemplateIds, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedTemplateIds.length} template(s) assigned successfully.`);
            await fetchTemplates();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to assign referral templates.');
        } finally {
            setAddLoading(false);
        }
    };

    const handleDeleteMapping = async () => {
        if (!selectedCompany || selectedRows.length === 0) return;
        const toastId = toast(
            <div style={{ textAlign: 'center', minWidth: '250px' }}>
                <div style={{ fontWeight: '600', marginBottom: '10px' }}>
                    Delete {selectedRows.length} mapping(s)?
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '10px' }}>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => toast.dismiss(toastId)}>
                        Cancel
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => { toast.dismiss(toastId); performDelete(); }}>
                        Yes, Delete
                    </button>
                </div>
            </div>,
            { autoClose: false, closeButton: false, closeOnClick: false, draggable: false, className: 'bg-white shadow' }
        );
    };

    const performDelete = async () => {
        setDeleteLoading(true);
        const loadingToast = toast.loading('Deleting mappings...');
        try {
            await deleteReferralTemplateMapping(selectedCompany.value, selectedRows, domain);
            toast.dismiss(loadingToast);
            toast.success(`${selectedRows.length} mapping(s) deleted successfully.`);
            await fetchTemplates();
        } catch (err) {
            toast.dismiss(loadingToast);
            toast.error(err.response?.data?.message || 'Failed to delete mappings.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleCompanyChange = (selected) => {
        setSelectedCompany(selected);
        setSelectedTemplateIds([]);
        setSelectedRows([]);
        setTemplateSearch('');
    };

    const filteredTemplates = referralTemplates.filter(t =>
        (t.templateName || '').toLowerCase().includes(templateSearch.toLowerCase())
    );

    const allMappedSelected = mappedTemplates.length > 0 && mappedTemplates.every(t => selectedRows.includes(t.templateId));

    const handleSelectAll = () => {
        if (allMappedSelected) setSelectedRows([]);
        else setSelectedRows(mappedTemplates.map(t => t.templateId));
    };

    return (
        <div className="container-fluid p-0 px-1">
            <div className="card-body">
                <div className="row g-2">
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={cardHeaderStyle}>
                                Add Templates
                            </div>
                            <div className="card-body p-2">
                                <div className="mb-2">
                                    <Field label="Company" required htmlFor="crt-company" />
                                    <Select
                                        inputId="crt-company"
                                        options={companies}
                                        value={selectedCompany}
                                        onChange={handleCompanyChange}
                                        isLoading={companiesLoading}
                                        placeholder="Select company..."
                                        isSearchable
                                        isClearable
                                        styles={selectStyles}
                                    />
                                </div>
                                <div className="mb-2">
                                    <Field label="Search Templates" htmlFor="crt-template-search" />
                                    <input
                                        id="crt-template-search"
                                        type="text"
                                        className="form-control form-control-sm crt-input"
                                        placeholder="Filter template list..."
                                        value={templateSearch}
                                        onChange={e => setTemplateSearch(e.target.value)}
                                        disabled={!selectedCompany}
                                        style={{ fontSize: '12px' }}
                                    />
                                </div>
                                <div style={{ height: '280px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', background: '#fafafa' }}>
                                    {templatesLoading ? (
                                        <Spinner />
                                    ) : filteredTemplates.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            {selectedCompany ? 'No unassigned templates.' : 'Select a company first.'}
                                        </div>
                                    ) : (
                                        filteredTemplates.map(t => {
                                            const checked = selectedTemplateIds.includes(t.templateId);
                                            return (
                                                <label
                                                    key={t.templateId}
                                                    className="crt-row"
                                                    style={{
                                                        display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px',
                                                        borderBottom: '1px solid #f1f5f9', cursor: 'pointer', fontSize: '12px',
                                                        background: checked ? THEME.primaryLight : undefined,
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() =>
                                                            setSelectedTemplateIds(prev =>
                                                                prev.includes(t.templateId)
                                                                    ? prev.filter(id => id !== t.templateId)
                                                                    : [...prev, t.templateId]
                                                            )
                                                        }
                                                        style={{ accentColor: THEME.primary }}
                                                    />
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {t.templateName}
                                                        </div>
                                                        {t.description && (
                                                            <div style={{ fontSize: '10px', color: '#6c757d', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {t.description}
                                                            </div>
                                                        )}
                                                    </div>
                                                </label>
                                            );
                                        })
                                    )}
                                </div>
                                <div className="d-flex justify-content-center gap-2 mt-2">
                                    <button
                                        onClick={() => { setSelectedTemplateIds([]); setTemplateSearch(''); }}
                                        className="btn btn-outline-secondary btn-sm px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        Reset
                                    </button>
                                    <button
                                        onClick={handleAddMapping}
                                        disabled={addLoading || templatesLoading || !selectedCompany || selectedTemplateIds.length === 0}
                                        className="btn btn-sm btn-theme px-3 fw-semibold"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {addLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Saving...
                                            </>
                                        ) : (
                                            `Add Mapping (${selectedTemplateIds.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-lg-6">
                        <div className="card border h-100" style={{ borderRadius: '6px' }}>
                            <div className="card-header" style={cardHeaderStyle}>
                                Mapped Templates
                            </div>
                            <div className="card-body p-2">
                                {mappedTemplates.length > 0 && (
                                    <div className="d-flex align-items-center justify-content-between mb-1 px-1 py-1"
                                        style={{ borderBottom: '1px solid #e9ecef', minHeight: '28px' }}>

                                        <div className="d-flex align-items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="selectAllMapped"
                                                checked={allMappedSelected}
                                                onChange={handleSelectAll}
                                                style={{ accentColor: THEME.primary, width: '14px', height: '14px', cursor: 'pointer' }}
                                            />
                                            <label className="form-check-label mb-0" style={{ fontSize: '11px', fontWeight: 600, color: '#495057', cursor: 'pointer', userSelect: 'none' }} htmlFor="selectAllMapped">
                                                Select All
                                            </label>
                                            <span style={{ fontSize: '10px', color: '#adb5bd' }}>
                                                ({mappedTemplates.length} total)
                                            </span>
                                        </div>

                                        {selectedRows.length > 0 && (
                                            <span style={{
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                background: THEME.primaryLight,
                                                color: THEME.primaryHover,
                                                padding: '2px 8px',
                                                borderRadius: '20px',
                                                border: `1px solid ${THEME.alpha(0.25)}`
                                            }}>
                                                ✓ {selectedRows.length} selected
                                            </span>
                                        )}
                                    </div>
                                )}
                                <div style={{ height: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px' }}>
                                    {templatesLoading ? (
                                        <Spinner />
                                    ) : mappedTemplates.length === 0 ? (
                                        <div className="text-center py-4" style={{ color: '#adb5bd', fontSize: '12px' }}>
                                            {selectedCompany ? 'No active mappings.' : 'Select a company first.'}
                                        </div>
                                    ) : (
                                        <table className="table table-sm table-hover mb-0" style={{ fontSize: '12px' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8f9fa', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '36px', padding: '6px 10px' }}><span className="visually-hidden">Select</span></th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Template Name</th>
                                                    <th style={{ padding: '6px 10px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.3px', color: '#6c757d' }}>Description</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {mappedTemplates.map(item => {
                                                    const rowSelected = selectedRows.includes(item.templateId);
                                                    const cellBg = rowSelected ? THEME.primaryLight : undefined;
                                                    return (
                                                        <tr key={item.templateId}>
                                                            <td style={{ padding: '6px 10px', background: cellBg }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={rowSelected}
                                                                    onChange={() =>
                                                                        setSelectedRows(prev =>
                                                                            prev.includes(item.templateId)
                                                                                ? prev.filter(id => id !== item.templateId)
                                                                                : [...prev, item.templateId]
                                                                        )
                                                                    }
                                                                    aria-label={`Select ${item.templateName}`}
                                                                    style={{ accentColor: THEME.primary }}
                                                                />
                                                            </td>
                                                            <td style={{ padding: '6px 10px', fontWeight: 500, background: cellBg }}>{item.templateName}</td>
                                                            <td style={{ padding: '6px 10px', color: '#6c757d', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', background: cellBg }}>
                                                                {item.description || '—'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                                <div className="d-flex justify-content-center mt-2">
                                    <button
                                        onClick={handleDeleteMapping}
                                        disabled={deleteLoading || templatesLoading || selectedRows.length === 0}
                                        className="btn btn-sm btn-danger px-3"
                                        style={{ fontSize: '12px' }}
                                    >
                                        {deleteLoading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                                Deleting...
                                            </>
                                        ) : (
                                            `Delete Selected (${selectedRows.length})`
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .crt-row:hover { background: ${THEME.alpha(0.06)}; }
                .crt-input:focus { border-color: ${THEME.primary}; box-shadow: 0 0 0 0.2rem ${THEME.alpha(0.2)}; }
            `}</style>
        </div>
    );
};

export default ConfigureReferralTemplate;